import {
  dayKeyOf,
  formatShortDate,
  tt,
  type LunchMeal,
  type LunchSettings,
  type LunchWeek,
} from '@agos/shared'
import { lunchDateKey } from '@agos/customer-core'

export { lunchSettingsOf, lunchWeeksOf } from '@agos/customer-core'

/**
 * Which days of the daily menu a customer may order from, in the order the tabs show them.
 *
 * Legacy looks a week ahead and keeps every day that has meals written for it, labelling the first two
 * *Dnes* and *Zajtra* — a preorder is read as *is that tomorrow or Thursday?*, so the weekday earns its
 * place and the date beside it settles the ambiguity.
 *
 * **The date is keyed by local components.** Legacy's own `getUpcomingLunchDays` builds the label with
 * `d.toISOString().slice(0, 10)`, which converts a Bratislava date to UTC first — so a customer opening
 * the page at 00:30 sees yesterday's date under *Dnes*. That is FB-034 again, the same trap the manager
 * hit in `mondayOf`.
 */

export interface LunchDayOption {
  /** `2026-07-31`, in the restaurant's own calendar. */
  key: string
  date: Date
  /** *Dnes*, *Zajtra*, or the weekday. */
  dayName: string
  /** `31. 07.` */
  shortDate: string
  meals: LunchMeal[]
}

const WEEKDAY_NAMES = [
  tt('Nedeľa'),
  tt('Pondelok'),
  tt('Utorok'),
  tt('Streda'),
  tt('Štvrtok'),
  tt('Piatok'),
  tt('Sobota'),
]

/** The meals written for one date, across whichever week holds it. */
export function mealsOn(weeks: readonly LunchWeek[], date: Date): LunchMeal[] {
  const key = dayKeyOf(date)
  const week = weekFor(weeks, date)
  if (week === undefined) return []
  for (const day of week.days) {
    if (day.dayKey === key && day.enabled && day.meals.length > 0) return day.meals
  }
  return []
}

/**
 * Which stored week a customer's date reads from - §9.399, and it is legacy's own two lines.
 *
 * <p><b>The week that holds the date, else the first one.</b> `lunch-store.ts:169` is
 * `weeks.find(w => w.startDate === targetMonday)` followed by `if (!week) week = weeks[0]`, so a
 * franchise that authored **one** week sees it every week - which is what `weekCount: 1` means, and
 * why nobody re-types the menu each Monday.
 *
 * <p><b>AGOS required the exact containment and had no fallback</b>, so the owner's menu - one week
 * stamped `2026-07-27` - went invisible on 2026-08-03 and stayed that way while
 * `settings.visible` read `true`. The containment check itself is kept and still wins: it is what
 * stops a two-week menu showing next week's Monday under today's tab, which is the fault it was
 * added for.
 *
 * <p>A week with no `startDate` at all claims every date, which is the shape a legacy blob written
 * before the field existed has.
 */
function weekFor(weeks: readonly LunchWeek[], date: Date): LunchWeek | undefined {
  const iso = lunchDateKey(date)
  const holding = weeks.find(
    (week) => week.startDate === undefined || week.startDate === '' || holds(week.startDate, iso),
  )
  return holding ?? weeks[0]
}

/**
 * Whether the restaurant serves lunch on this date at all — `FRONTEND.md` §9.83.
 *
 * Deliberately **not** *"are there meals written"*: a serving day nobody has filled in yet still
 * earns its tab, which is §9.17's rule and stays. This asks the narrower question — is this weekday
 * part of the lunch week the franchise configured?
 *
 * The owner's weeks carry `mon`–`fri` and nothing else, so a disabled weekend is an **absent** day
 * row rather than `enabled: false`. Both are treated the same, because both mean *we do not serve
 * lunch then*.
 */
export function servesLunchOn(weeks: readonly LunchWeek[], date: Date): boolean {
  const key = dayKeyOf(date)
  // §9.399 - the same week this date's meals come from, so the tab and its contents cannot disagree.
  const week = weekFor(weeks, date)
  if (week === undefined) return false
  return week.days.some((day) => day.dayKey === key && day.enabled)
}

function holds(weekStart: string, iso: string): boolean {
  const start = Date.parse(`${weekStart}T00:00:00Z`)
  const day = Date.parse(`${iso}T00:00:00Z`)
  if (!Number.isFinite(start) || !Number.isFinite(day)) return true
  const offset = (day - start) / 86_400_000
  return offset >= 0 && offset < 7
}

/**
 * The six days a customer may order from — today and the next five (`FRONTEND.md` §9.83).
 *
 * **§9.17 said five, and the owner has since written six**: *"I still should see current + next 5
 * days."* The later sentence is the instruction; `ahead` is the whole difference, and §9.17's
 * argument below is unchanged by the count.
 *
 * **A fixed window, not a filtered one.** This looked seven days ahead and skipped any with nothing
 * written, so a Wednesday showed three tabs and a Friday showed one. Legacy does the same. The owner
 * asked for *"always 5 days (current + 4)"*, and a day inside the window with no menu is now shown
 * and marked rather than dropped: *nothing on Thursday* is an answer, and a tab that silently
 * disappears is one a customer cannot tell from a page that failed to load.
 *
 * A day with no meals carries an empty `meals`, so every caller that reads `option.meals` keeps
 * working — {@link lunchSections} answers no sections for it and the modal shows its empty state.
 */
export function upcomingLunchDays(
  weeks: readonly LunchWeek[],
  now: Date,
  ahead = 6,
  horizon = 21,
): LunchDayOption[] {
  const days: LunchDayOption[] = []
  // §9.83 — a franchise that has configured no lunch week at all keeps the old fixed window. Walking
  // forward would find nothing to collect and leave no tabs, which reads as a page that failed to
  // load — the very failure §9.17 was written against.
  const anyServingDay = weeks.some((week) => week.days.some((day) => day.enabled))

  for (let offset = 0; offset < horizon && days.length < ahead; offset += 1) {
    const date = new Date(now)
    date.setDate(date.getDate() + offset)
    // §9.83 — the owner's weekends are absent from every week, so they are not serving days and earn
    // no tab. A serving day with nothing written still gets one: §9.17, unchanged.
    if (anyServingDay && !servesLunchOn(weeks, date)) continue
    const meals = mealsOn(weeks, date)
    days.push({
      key: lunchDateKey(date),
      date,
      // Named by the real distance from today, not by position in the strip: when Saturday and
      // Sunday are skipped, Monday is the third tab and calling it *Zajtra* would be a lie.
      dayName: offset === 0 ? tt('Dnes') : offset === 1 ? tt('Zajtra') : (WEEKDAY_NAMES[date.getDay()] ?? ''),
      shortDate: formatShortDate(lunchDateKey(date)),
      meals,
    })
  }
  return days
}


/**
 * The sections this day holds, in the order the manager arranged them - §9.400.
 *
 * **Each section appears where its first meal appears.** The lunch editor renders a day as
 * `day.meals.map(...)` with an up and a down button per row, and §9.133's comment beside those
 * buttons says what they are for: *"the order decides the sequence on the customer's menu, the
 * printed sheet and the lunch tab"*. The printed sheet keeps that promise - `LunchPrintView` groups
 * into a `Map` over `day.meals` and renders `[...byType.entries()]`, which is insertion order. This
 * function used to compose a fixed list, `[...ALL_SECTION_TYPES, ...customSections, main]`, and
 * filter it down to the types the day held, so the sequence came from a literal written here and
 * the owner's arrangement was discarded. Measured on their data: they moved the `burger` meals ahead
 * of the `pizza` meals and renamed each heading to the other's word, so the manager read *Polievky,
 * PIZZA, Burger* and the customer read *Polievky, Burger, PIZZA* off the same row.
 *
 * **A day is the only thing that can hold the answer.** `soup` is both first and last on three of
 * this franchise's days, and *Šaláty third from the end on Thursday* is not expressible as a global
 * ordering, so there is no list to sort instead.
 *
 * **And a meal filed under a deleted section stops vanishing.** The old composition could only emit
 * a section it recognised, so a meal whose `type` is a custom section since removed matched no entry
 * and the food disappeared from the customer menu while the manager kept showing it - §9.315 gives
 * that exact case its own `<option>`. Walking the meals means the section is emitted because a meal
 * is in it, which is the only condition that matters.
 */
export function lunchSections(
  settings: LunchSettings,
  meals: readonly LunchMeal[],
): Array<{ type: string; label: string }> {
  const sections: Array<{ type: string; label: string }> = []
  const seen = new Set<string>()
  for (const meal of meals) {
    if (seen.has(meal.type)) continue
    seen.add(meal.type)
    sections.push({ type: meal.type, label: sectionLabel(settings, meal.type) })
  }
  return sections
}

/**
 * The heading one section carries here - the franchise's rename, then this app's own word.
 *
 * Deliberately **not** the manager's label: `defaultLabel` says *Šaláty* over a list of salads where
 * the editor's row says *Šalát*, and `main` has no built-in entry at all. Its `default: return type`
 * is what names a section the settings no longer describe, rather than drawing a blank heading.
 */
function sectionLabel(settings: LunchSettings, type: string): string {
  const renamed = settings.sectionLabels[type]
  if (renamed) return renamed
  const own = settings.customSections?.find((section) => section.type === type)
  if (own) return own.label
  if (type === 'main') return tt('Hlavné jedlá')
  return defaultLabel(type)
}

function defaultLabel(type: string): string {
  switch (type) {
    case 'soup':
      return tt('Polievky')
    case 'pizza':
      return 'Pizza'
    case 'burger':
      return tt('Burgre')
    case 'bowl':
      return tt('Bowle')
    case 'salad':
      return tt('Šaláty')
    case 'pasta-gnocchi':
      return tt('Cestoviny - Gnocchi')
    case 'pasta-bolognese':
      return tt('Cestoviny - Bolonské')
    case 'dessert':
      return tt('Dezerty')
    default:
      return type
  }
}
